import React from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PropTypes from 'prop-types';
import { I18n } from 'react-redux-i18n';
import { getAccessCopyKeys } from './accessCopy';
import { getErrorCopyKey } from './errorCopy';
import ChatComposer from './ChatComposer';
import DobCalendarHeader from './DobCalendarHeader';
import MessageList from './MessageList';
import NawiChatProvider, {
  useNawiChatActions,
  useNawiChatComposer,
  useNawiChatConversation,
} from './NawiChatContext';
import {
  getDobMaxDate,
  getDobMinDate,
  getDobPickerConfig,
  getDobYears,
} from './dobDate';
import { NawiMark, SpinnerOverlay } from './NawiIdentity';
import WelcomeChat from './WelcomeChat';
import './NawiChat.css';

// The tab pane around the chat scrolls (overflow-y: auto) and would clip an
// absolutely positioned calendar, so it is rendered in the body instead.
const DobPopperContainer = ({ children }) =>
  createPortal(children, document.body);

DobPopperContainer.propTypes = { children: PropTypes.node };

const renderDobHeader = (monthNames) => (headerProps) => (
  <DobCalendarHeader
    {...headerProps}
    monthNames={monthNames}
    years={getDobYears()}
  />
);

const PersonContextGate = () => {
  const {
    handleDobChange,
    handleDobRawChange,
    handleDobSubmit,
  } = useNawiChatActions();
  const {
    accessDeniedCode,
    contextError,
    contextStatus,
    dobValue,
    personErrorCode,
  } = useNawiChatComposer();

  if (contextStatus === 'loading') return <SpinnerOverlay />;

  if (contextStatus === 'dob_required') {
    const { dateFormat, locale, monthNames } = getDobPickerConfig();

    return (
      <form
        className="nawi-chat__context-gate nawi-chat__context-gate--dob"
        onSubmit={handleDobSubmit}
      >
        <h3 className="nawi-chat__context-title">
          {I18n.t('persons.nawiChat.dobRequired.title')}
        </h3>
        <p>{I18n.t('persons.nawiChat.dobRequired.message')}</p>
        <DatePicker
          autoComplete="off"
          customInput={
            <input aria-label={I18n.t('persons.nawiChat.dobRequired.label')} />
          }
          dateFormat={dateFormat}
          id="nawi-person-dob"
          locale={locale}
          maxDate={getDobMaxDate()}
          minDate={getDobMinDate()}
          onChange={handleDobChange}
          onChangeRaw={handleDobRawChange}
          placeholderText={I18n.t('persons.nawiChat.dobRequired.placeholder')}
          popperClassName="nawi-chat__dob-popper"
          popperContainer={DobPopperContainer}
          popperPlacement="bottom-start"
          renderCustomHeader={renderDobHeader(monthNames)}
          required
          selected={dobValue}
          // Lenient parsing reads a half-typed year (25/12/19) as 2019.
          strictParsing
        />
        {contextError && (
          <p role="alert">{I18n.t('persons.nawiChat.dobRequired.invalid')}</p>
        )}
        <button className="nawi-chat__context-submit" type="submit">
          {I18n.t('persons.nawiChat.dobRequired.action')}
        </button>
      </form>
    );
  }

  if (contextStatus === 'not_enabled') {
    const copy = getAccessCopyKeys(accessDeniedCode);

    return (
      <div
        className="nawi-chat__context-gate nawi-chat__context-gate--not-enabled"
        role="status"
      >
        <p>{I18n.t(copy.title)}</p>
        {copy.body && <p>{I18n.t(copy.body)}</p>}
      </div>
    );
  }

  if (contextStatus === 'person_unavailable') {
    return (
      <div className="nawi-chat__context-gate" role="alert">
        <p>{I18n.t(getErrorCopyKey(personErrorCode))}</p>
      </div>
    );
  }

  return (
    <div className="nawi-chat__context-gate" role="alert">
      <p>{I18n.t('chatbot.error')}</p>
    </div>
  );
};

const NawiChatContent = ({ height, isAuthenticating }) => {
  const { handleWorkspaceScroll } = useNawiChatActions();
  const { contextStatus } = useNawiChatComposer();
  const { messages } = useNawiChatConversation();
  const isPreparing = isAuthenticating || contextStatus !== 'ready';
  const isBusy = isAuthenticating || contextStatus === 'loading';

  return (
    <section
      aria-label={I18n.t('persons.nawiChat.regionLabel')}
      aria-busy={isBusy}
      className="card nawi-chat"
      style={{ height }}
    >
      {isPreparing ? (
        isAuthenticating ? <SpinnerOverlay /> : <PersonContextGate />
      ) : (
        <>
          <header className="nawi-chat__header">
            <NawiMark size={18} />
            <span>{I18n.t('persons.nawiChat.model')}</span>
          </header>

          <div
            aria-live="polite"
            className={`nawi-chat__workspace ${
              messages.length ? 'nawi-chat__workspace--conversation' : ''
            }`}
            onScroll={handleWorkspaceScroll}
          >
            {messages.length === 0 ? <WelcomeChat /> : <MessageList />}
          </div>

          <ChatComposer />
        </>
      )}
    </section>
  );
};

NawiChatContent.propTypes = {
  height: PropTypes.number,
  isAuthenticating: PropTypes.bool.isRequired,
};

const NawiChat = ({ height, isAuthenticating = false, person }) => (
  <NawiChatProvider
    accountId={person?.campaign?.branch?.account?.id}
    userPlayerId={person?.user_player?.id}
  >
    <NawiChatContent height={height} isAuthenticating={isAuthenticating} />
  </NawiChatProvider>
);

NawiChat.propTypes = {
  height: PropTypes.number,
  isAuthenticating: PropTypes.bool,
  person: PropTypes.object,
};

export default NawiChat;
